import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import safeToast from "utils/safeToast";

// MUI Components
import {
  Box,
  Paper,
  Typography,
  Avatar,
  TextField,
  InputAdornment,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  IconButton,
  Badge,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ChatIcon from "@mui/icons-material/Chat";
import GroupIcon from "@mui/icons-material/Group";
import SendIcon from "@mui/icons-material/Send";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// Custom Components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Components
import PrivateChat from "./PrivateChat";

// Services
import friendService from "API/friendService";
import privateChatService from "API/privateChatService";
import groupChatService from "API/groupChatService";
import signalRManager from "services/signalRManager";
import { useSignalR } from "hooks/useSignalR";
import { getFullUrl } from "config/api";
import { decryptText } from "config/encryption";

const GroupChatPanel = ({ group, currentStudentId, onClose }) => {
  const { isConnected, onlineUsers } = useSignalR();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [groupInfoOpen, setGroupInfoOpen] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const messagesEndRef = React.useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const loadHistory = useCallback(async () => {
    if (!group?.groupId) return;
    setLoading(true);
    try {
      const res = await groupChatService.getHistory(group.groupId, 1, 100);
      setHistory(res?.messages || []);
      setTimeout(() => scrollToBottom(), 50);
    } catch (error) {
      console.error("Failed to load group history:", error);
      safeToast.error("Failed to load group messages");
    } finally {
      setLoading(false);
    }
  }, [group, scrollToBottom]);

  useEffect(() => {
    if (!group?.groupId) return;
    let mounted = true;
    (async () => {
      await signalRManager.startConnection();
      await signalRManager.joinGroup(group.groupId);
      if (mounted) {
        await loadHistory();
      }
    })();

    const handleGroupMessage = (msg) => {
      if (!msg || msg.groupId !== group.groupId) return;
      setHistory((prev) => {
        if (prev.some((m) => m.messageId === msg.messageId)) return prev;
        return [...prev, msg];
      });
      setTimeout(() => scrollToBottom(), 50);
    };

    signalRManager.on("onGroupMessage", handleGroupMessage);

    return () => {
      mounted = false;
      signalRManager.off("onGroupMessage", handleGroupMessage);
      signalRManager.leaveGroup(group.groupId);
    };
  }, [group, loadHistory, scrollToBottom]);

  const handleSend = async () => {
    const text = message.trim();
    if (!group?.groupId || !text || sending) return;
    setSending(true);
    try {
      let ok = false;
      if (isConnected) {
        ok = await signalRManager.sendGroupMessage(group.groupId, text, null, null);
      }

      if (!ok) {
        const saved = await groupChatService.sendMessage(group.groupId, text, null, null);
        if (saved?.messageId) {
          setHistory((prev) => {
            if (prev.some((m) => m.messageId === saved.messageId)) return prev;
            return [...prev, saved];
          });
        }
      }
      setMessage("");
      setTimeout(() => scrollToBottom(), 50);
    } catch (error) {
      console.error("Failed to send group message:", error);
      safeToast.error("Failed to send group message");
    } finally {
      setSending(false);
    }
  };

  const isMemberOnline = useCallback((member) => {
    return (onlineUsers || []).some((u) => {
      const onlineIsStaff = !!u.isStaff;
      const onlineStudentId = u.studentId ?? u.StudentId ?? null;
      const onlineStaffId = u.staffId ?? u.StaffId ?? null;
      if (member.isStaff) {
        return onlineIsStaff && Number(onlineStaffId) === Number(member.staffId);
      }
      return !onlineIsStaff && Number(onlineStudentId) === Number(member.studentId);
    });
  }, [onlineUsers]);

  const openGroupInfo = async () => {
    setGroupInfoOpen(true);
    setLoadingMembers(true);
    try {
      const data = await groupChatService.getMembers(group.groupId);
      setGroupMembers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load group members:", error);
      safeToast.error("Failed to load group members");
      setGroupMembers([]);
    } finally {
      setLoadingMembers(false);
      setMenuAnchor(null);
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Paper elevation={1} sx={{ p: 2, display: "flex", alignItems: "center", gap: 1, borderRadius: 0 }}>
        {onClose && (
          <IconButton onClick={onClose} size="small">
            <ChatIcon />
          </IconButton>
        )}
        <Avatar
          src={group.groupImageUrl ? getFullUrl(group.groupImageUrl) : null}
          sx={{ width: 28, height: 28, bgcolor: "info.main" }}
        >
          <GroupIcon sx={{ fontSize: 16 }} />
        </Avatar>
        <Box flex={1}>
          <Typography variant="body1" fontWeight={600} sx={{ fontSize: "0.95rem" }}>
            {group.groupName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {group.memberCount || 0} members
          </Typography>
        </Box>
        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Paper>

      <Box sx={{ flex: 1, overflowY: "auto", p: 2, bgcolor: (theme) => alpha(theme.palette.background.default, 0.5) }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={180}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">No messages in this group yet</Typography>
          </Box>
        ) : (
          history.map((m) => {
            const isMine = m.studentId === currentStudentId;
            return (
              <Box key={m.messageId} display="flex" justifyContent={isMine ? "flex-end" : "flex-start"} mb={1}>
                <Paper
                  sx={{
                    p: 1.25,
                    maxWidth: "70%",
                    bgcolor: isMine ? "success.main" : "background.paper",
                    color: isMine ? "success.contrastText" : "text.primary",
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600, fontSize: "0.7rem" }}>
                    {m.displayName || m.userName || "User"}
                  </Typography>
                  {m.message && (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontSize: "0.82rem" }}>
                      {m.message}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ opacity: 0.7, fontSize: "0.68rem" }}>
                    {m.sentDateTime ? new Date(m.sentDateTime).toLocaleTimeString() : ""}
                  </Typography>
                </Paper>
              </Box>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Paper elevation={2} sx={{ p: 1.5, borderRadius: 0 }}>
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            fullWidth
            size="small"
            placeholder="Type a message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <IconButton
            color="success"
            onClick={handleSend}
            disabled={!message.trim() || sending}
            sx={{ bgcolor: "success.main", color: "white", "&:hover": { bgcolor: "success.dark" } }}
          >
            {sending ? <CircularProgress size={18} sx={{ color: "white" }} /> : <SendIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Paper>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={openGroupInfo}>
          <InfoOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
          Group info
        </MenuItem>
      </Menu>

      <Dialog open={groupInfoOpen} onClose={() => setGroupInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Group info</DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center" gap={1.2}>
            <Avatar src={group.groupImageUrl ? getFullUrl(group.groupImageUrl) : null} sx={{ width: 42, height: 42 }}>
              {(group.groupName || "?").charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {group.groupName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {group.memberCount || groupMembers.length || 0} members
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 1.5 }} />
          {loadingMembers ? (
            <Box py={2} display="flex" justifyContent="center">
              <CircularProgress size={20} />
            </Box>
          ) : (
            groupMembers.map((member) => {
              const isOwner = !!member.isStaff && Number(member.staffId) === Number(group.createdByStaffId);
              const online = isMemberOnline(member);
              return (
                <Box key={`${member.isStaff ? "S" : "U"}-${member.staffId || member.studentId}`} display="flex" alignItems="center" py={0.75}>
                  <Avatar src={member.profileImageUrl ? getFullUrl(member.profileImageUrl) : null} sx={{ width: 32, height: 32, mr: 1.2 }}>
                    {(member.displayName || member.userName || "?").charAt(0).toUpperCase()}
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600}>
                      {member.displayName || member.userName || "Member"}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.6}>
                      <FiberManualRecordIcon sx={{ fontSize: 9, color: online ? "success.main" : "text.disabled" }} />
                      <Typography variant="caption" color="text.secondary">
                        {online ? "Online" : "Offline"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        • {isOwner ? "Owner" : member.isStaff ? "Admin" : "Member"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

GroupChatPanel.propTypes = {
  group: PropTypes.shape({
    groupId: PropTypes.number.isRequired,
    groupName: PropTypes.string,
    groupImageUrl: PropTypes.string,
    memberCount: PropTypes.number,
    createdByStaffId: PropTypes.number,
  }).isRequired,
  currentStudentId: PropTypes.number,
  onClose: PropTypes.func,
};

const WhatsAppChat = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { connection, isConnected, onlineUsers } = useSignalR();

  const [currentStudentId, setCurrentStudentId] = useState(null);
  const [friends, setFriends] = useState([]);
  const [filteredFriends, setFilteredFriends] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});

  // Get current user ID
  useEffect(() => {
    const userDetails = localStorage.getItem("UserLogDetails");
    if (userDetails) {
      try {
        const decrypted = decryptText(userDetails);
        const { StudentId } = JSON.parse(decrypted);
        setCurrentStudentId(StudentId);
      } catch (error) {
        console.error("Error parsing user details:", error);
      }
    }
  }, []);

  // Load friends
  const loadFriends = useCallback(async () => {
    if (!currentStudentId) return;
    
    setLoading(true);
    try {
      const data = await friendService.getFriends();
      setFriends(data || []);
      setFilteredFriends(data || []);
      
      // Load unread counts
      if (data && data.length > 0) {
        const counts = {};
        for (const friend of data) {
          const requesterId = friend.RequesterId || friend.requesterId;
          const receiverId = friend.ReceiverId || friend.receiverId;
          const friendId = requesterId === currentStudentId ? receiverId : requesterId;
          
          if (friendId && friendId !== currentStudentId) {
            try {
              const countResponse = await privateChatService.getUnreadCount(friendId);
              const count = countResponse?.count || countResponse || 0;
              counts[friendId] = count;
            } catch (error) {
              console.error(`Error loading unread count for friend ${friendId}:`, error);
              counts[friendId] = 0;
            }
          }
        }
        setUnreadCounts(counts);
      }
    } catch (error) {
      try {
        safeToast.error("Failed to load friends");
      } catch (toastErr) {
        console.error("Toast error:", toastErr);
      }
      console.error("Error loading friends:", error);
    } finally {
      setLoading(false);
    }
  }, [currentStudentId]);

  useEffect(() => {
    if (currentStudentId) {
      loadFriends();
    }
  }, [currentStudentId, loadFriends]);

  // Load group chats for current alumni user
  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const data = await groupChatService.getGroups();
      setGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading groups:", error);
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  useEffect(() => {
    if (currentStudentId) {
      loadGroups();
    }
  }, [currentStudentId, loadGroups]);

  // Filter friends based on search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredFriends(friends);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = friends.filter((friend) => {
      const requesterId = friend.RequesterId || friend.requesterId;
      const receiverId = friend.ReceiverId || friend.receiverId;
      const friendId = requesterId === currentStudentId ? receiverId : requesterId;
      
      const friendName = requesterId === currentStudentId
        ? (friend.ReceiverName || friend.receiverName || "")
        : (friend.RequesterName || friend.requesterName || "");
      const friendUserName = requesterId === currentStudentId
        ? (friend.ReceiverUserName || friend.receiverUserName || "")
        : (friend.RequesterUserName || friend.requesterUserName || "");
      
      return (
        friendName.toLowerCase().includes(query) ||
        friendUserName.toLowerCase().includes(query)
      );
    });
    setFilteredFriends(filtered);
  }, [searchQuery, friends, currentStudentId]);

  // Update unread count for a specific friend
  const updateUnreadCount = useCallback(async (friendId) => {
    if (!friendId || friendId === currentStudentId) return;
    
    try {
      const countResponse = await privateChatService.getUnreadCount(friendId);
      const count = countResponse?.count || countResponse || 0;
      setUnreadCounts((prev) => ({ ...prev, [friendId]: count }));
    } catch (error) {
      console.error(`Error updating unread count for friend ${friendId}:`, error);
    }
  }, [currentStudentId]);

  // SignalR event handlers
  useEffect(() => {
    if (!connection || !isConnected || !currentStudentId) return;

    const handleReceivePrivateMessage = async (privateMessage) => {
      if (privateMessage && privateMessage.toStudentId === currentStudentId) {
        const senderId = privateMessage.fromStudentId;
        if (senderId) {
          await updateUnreadCount(senderId);
        }
      }
    };

    const handlePrivateMessageRead = async (readMessage) => {
      if (readMessage && readMessage.fromStudentId === currentStudentId) {
        const recipientId = readMessage.toStudentId;
        if (recipientId) {
          await updateUnreadCount(recipientId);
        }
      }
    };

    connection.on("ReceivePrivateMessage", handleReceivePrivateMessage);
    connection.on("PrivateMessageRead", handlePrivateMessageRead);

    return () => {
      connection.off("ReceivePrivateMessage", handleReceivePrivateMessage);
      connection.off("PrivateMessageRead", handlePrivateMessageRead);
    };
  }, [connection, isConnected, currentStudentId, updateUnreadCount]);

  // Check if user is online
  const isUserOnline = useCallback((studentId) => {
    if (!onlineUsers || onlineUsers.length === 0) return false;
    return onlineUsers.some(user => {
      const userId = user.studentId || user.StudentId;
      const userIdNum = typeof userId === 'string' ? parseInt(userId) : userId;
      const targetId = typeof studentId === 'string' ? parseInt(studentId) : studentId;
      return userIdNum === targetId;
    });
  }, [onlineUsers]);

  // Handle friend selection
  const handleFriendSelect = (friend) => {
    setSelectedGroup(null);
    const requesterId = friend.RequesterId || friend.requesterId;
    const receiverId = friend.ReceiverId || friend.receiverId;
    const friendId = requesterId === currentStudentId ? receiverId : requesterId;
    
    const friendName = requesterId === currentStudentId
      ? (friend.ReceiverName || friend.receiverName || "Unknown")
      : (friend.RequesterName || friend.requesterName || "Unknown");
    
    const receiverImageUrl = friend.ReceiverProfileImageUrl || friend.receiverProfileImageUrl;
    const requesterImageUrl = friend.RequesterProfileImageUrl || friend.requesterProfileImageUrl;
    const profileImageUrl = requesterId === currentStudentId ? receiverImageUrl : requesterImageUrl;
    
    const friendUserName = requesterId === currentStudentId
      ? (friend.ReceiverUserName || friend.receiverUserName)
      : (friend.RequesterUserName || friend.requesterUserName);
    
    const imageUrl = profileImageUrl
      ? profileImageUrl.startsWith('http')
        ? profileImageUrl
        : getFullUrl(profileImageUrl)
      : null;

    setSelectedFriend({
      friendId,
      friendName,
      profileImageUrl: imageUrl,
      friendUserName,
    });
    
    // Clear unread count when chat opens
    if (friendId) {
      setUnreadCounts((prev) => ({ ...prev, [friendId]: 0 }));
      setTimeout(() => {
        updateUnreadCount(friendId);
      }, 1000);
    }
  };

  const handleGroupSelect = (group) => {
    setSelectedFriend(null);
    setSelectedGroup(group);
  };

  // Get last message preview (optional - can be enhanced later)
  const getLastMessagePreview = (friend) => {
    // This can be enhanced to show last message from chat history
    return "";
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box
        sx={{
          height: "calc(100vh - 120px)",
          display: "flex",
          bgcolor: theme.palette.background.default,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Left Sidebar - Friends List */}
        <Box
          sx={{
            width: isMobile && selectedFriend ? 0 : { xs: "100%", md: "400px" },
            display: { xs: isMobile && selectedFriend ? "none" : "flex", md: "flex" },
            flexDirection: "column",
            bgcolor: theme.palette.background.paper,
            borderRight: "1px solid",
            borderColor: "divider",
            transition: "width 0.3s ease",
            overflow: "hidden",
          }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderRadius: 0,
            borderBottom: "1px solid",
            borderColor: theme.palette.divider,
          }}
        >
          <Typography variant="body1" fontWeight={600} sx={{ fontSize: "0.9375rem" }}>
            Chat
          </Typography>
        </Paper>

        {/* Search Bar */}
        <Box sx={{ p: 1.5, bgcolor: theme.palette.background.default }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search or start new chat"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
                "&:hover fieldset": {
                  border: "none",
                },
                "&.Mui-focused fieldset": {
                  border: "none",
                },
              },
            }}
          />
        </Box>

        {/* Friends List */}
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {/* Groups block */}
          <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="caption" sx={{ fontSize: "0.75rem", fontWeight: 700, color: "text.secondary" }}>
                GROUPS
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontSize: "0.72rem", color: "primary.main", cursor: "pointer" }}
                onClick={loadGroups}
              >
                Refresh
              </Typography>
            </Box>
            {loadingGroups ? (
              <Box py={1} display="flex" justifyContent="center">
                <CircularProgress size={16} />
              </Box>
            ) : groups.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No groups available
              </Typography>
            ) : (
              <List sx={{ p: 0 }}>
                {groups.map((group) => {
                  const isSelected = selectedGroup?.groupId === group.groupId;
                  return (
                    <ListItem key={group.groupId} disablePadding>
                      <ListItemButton
                        onClick={() => handleGroupSelect(group)}
                        sx={{
                          borderRadius: 1,
                          px: 1,
                          py: 0.8,
                          mb: 0.5,
                          bgcolor: isSelected ? alpha(theme.palette.success.main, 0.12) : "transparent",
                        }}
                      >
                        <ListItemAvatar sx={{ minWidth: 34 }}>
                          <Avatar
                            src={group.groupImageUrl ? getFullUrl(group.groupImageUrl) : null}
                            sx={{ width: 26, height: 26, bgcolor: "info.main" }}
                          >
                            {(group.groupName || "?").charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 600 }} noWrap>
                              {group.groupName}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ fontSize: "0.68rem" }}>
                              {group.memberCount || 0} members
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>

          <Divider />

          {/* Friends block */}
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : filteredFriends.length === 0 ? (
            <Box textAlign="center" py={4}>
              <ChatIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
              <Typography color="text.secondary">No friends found</Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {filteredFriends.map((friend, index) => {
                const requesterId = friend.RequesterId || friend.requesterId;
                const receiverId = friend.ReceiverId || friend.receiverId;
                const friendId = requesterId === currentStudentId ? receiverId : requesterId;
                
                const friendName = requesterId === currentStudentId
                  ? (friend.ReceiverName || friend.receiverName || "Unknown")
                  : (friend.RequesterName || friend.requesterName || "Unknown");
                
                const receiverImageUrl = friend.ReceiverProfileImageUrl || friend.receiverProfileImageUrl;
                const requesterImageUrl = friend.RequesterProfileImageUrl || friend.requesterProfileImageUrl;
                const profileImageUrl = requesterId === currentStudentId ? receiverImageUrl : requesterImageUrl;
                
                const imageUrl = profileImageUrl
                  ? profileImageUrl.startsWith('http')
                    ? profileImageUrl
                    : getFullUrl(profileImageUrl)
                  : null;
                
                const isSelected = selectedFriend?.friendId === friendId;
                const isOnline = isUserOnline(friendId);
                const unreadCount = unreadCounts[friendId] || 0;

                return (
                  <React.Fragment key={friend.RequestId || index}>
                    <ListItem
                      disablePadding
                      sx={{
                        bgcolor: isSelected ? alpha(theme.palette.success.main, 0.1) : "transparent",
                        "&:hover": {
                          bgcolor: isSelected
                            ? alpha(theme.palette.success.main, 0.15)
                            : alpha(theme.palette.action.hover, 0.5),
                        },
                      }}
                    >
                      <ListItemButton
                        onClick={() => handleFriendSelect(friend)}
                        sx={{
                          py: 1.5,
                          px: 2,
                        }}
                      >
                        <ListItemAvatar>
                          <Badge
                            overlap="circular"
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            badgeContent={
                              isOnline ? (
                                <FiberManualRecordIcon
                                  sx={{
                                    fontSize: 12,
                                    color: "success.main",
                                    bgcolor: theme.palette.background.paper,
                                    borderRadius: "50%",
                                  }}
                                />
                              ) : null
                            }
                          >
                            <Avatar
                              src={imageUrl}
                              sx={{
                                width: 50,
                                height: 50,
                              }}
                            >
                              {friendName.charAt(0).toUpperCase()}
                            </Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Typography
                                variant="body2"
                                fontWeight={unreadCount > 0 ? 600 : 400}
                                sx={{
                                  fontSize: "0.8125rem",
                                  color: "text.primary",
                                }}
                              >
                                {friendName}
                              </Typography>
                              {unreadCount > 0 && (
                                <Badge
                                  badgeContent={unreadCount > 99 ? "99+" : unreadCount}
                                  color="error"
                                  sx={{
                                    "& .MuiBadge-badge": {
                                      fontSize: "0.7rem",
                                      height: 20,
                                      minWidth: 20,
                                      fontWeight: 600,
                                    },
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.75rem",
                                color: "text.secondary",
                                mt: 0.5,
                              }}
                            >
                              {isOnline ? "Online" : "Offline"}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < filteredFriends.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
        </Box>

        {/* Right Side - Chat Interface */}
        <Box
          sx={{
            flex: 1,
            display: { xs: selectedFriend ? "flex" : "none", md: "flex" },
            flexDirection: "column",
            bgcolor: theme.palette.background.paper,
            position: "relative",
          }}
        >
        {selectedGroup ? (
          <GroupChatPanel
            group={selectedGroup}
            currentStudentId={currentStudentId}
            onClose={isMobile ? () => setSelectedGroup(null) : null}
          />
        ) : selectedFriend ? (
          <PrivateChat
            otherStudentId={selectedFriend.friendId}
            otherStudentName={selectedFriend.friendName}
            otherProfileImageUrl={selectedFriend.profileImageUrl}
            onClose={isMobile ? () => setSelectedFriend(null) : null}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
            }}
          >
            <ChatIcon sx={{ fontSize: 120, color: theme.palette.text.disabled, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" fontWeight={300}>
              Select a friend or group to start chatting
            </Typography>
          </Box>
        )}
        </Box>
      </Box>
      <Footer />
    </DashboardLayout>
  );
};

export default WhatsAppChat;

